import React, { useMemo, useState, useEffect } from 'react';
import ScrollToTop from './helpers/ScrollToTop';
import Navbar from './components/Navbar/Navbar';
import { Routes, Route } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { userProfile, fetchUserSpecificData } from './features/auth/authAction';
import getUserToken from './helpers/tokenValidation';

import Home from './pages/Home';
import ServiceGroom from './components/ServiceGroom/ServiceGroom';
import ServiceBride from './components/ServiceBride/ServiceBride';
import Products from './pages/Products';
import SignUp from './pages/SignUp';
import Login from './pages/Login';
import EmailVerification from './pages/EmailVerification';
import Dashboard from './pages/Dashboard';
import UserDetails from './components/UserDetails/UserDetails';
import UserProfile from './components/UserProfile/UserProfile';
import ProtectedRoute from './routing/ProtectedRoutes';
import MoreDetailsPage from './components/MoreDetailsPage/MoreDetailsPage';
import PricingPage from './pages/PricingPage';
import PaymentPage from './pages/Payment';
import ActionAlerts from './helpers/alertBox';
import About from './pages/About';
import FavoriteListView from './components/FavoriteListView/FavoriteListView';
import PhAuth from './pages/phoneAuth/PhAuth';
import InforPage from './pages/InforPage';
import PermissionHandle from './pages/PermissionHandle';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import Footer from './components/Footer/Footer';
import DayFinder from './pages/day-finder/DayFinder';
import PaymentDay from './pages/day-finder/Payment-day';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import ChangePassword from './pages/ChangePassword';
import ChatPopup from './chat/ChatPopup';
import { Fab, Box, Badge, Tooltip, keyframes, Popover, Typography, Button, IconButton } from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloseIcon from '@mui/icons-material/Close';
import TermsAndConditions from './pages/TermsAndConditions';
import { socket, connectWithToken } from './chat/socket';
import { fetchUnreadCount } from './features/chatRedux/chatAction';
import { incrementUnreadCount } from './features/chatRedux/chatSlice';

function App() {
  const dispatch = useDispatch();
  const mode = 'light'; // ✅ Always light mode
  // const [mode, setMode] = useState(() => localStorage.getItem('mode') || 'light'); // REMOVED
  const [themeName, setThemeName] = useState(() => localStorage.getItem('themeName') || 'modern');
  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'si');
  const [chatOpen, setChatOpen] = useState(false);

  // Onboarding popover state
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingAnchor, setOnboardingAnchor] = useState(null);

  // Get user IDs from localStorage
  const { loggedUserInfo } = useSelector((state) => state.auth);
  const loggedUId = loggedUserInfo?.user_id || localStorage.getItem('user_id');
  const detailId = localStorage.getItem('detailId');
  const userName = localStorage.getItem('user_name') || 'Chat User';
  const unreadCount = useSelector((state) => state.chat?.unreadCount || 0);

  // Restore user session on app mount if token exists
  useEffect(() => {
    const token = getUserToken();
    const userId = localStorage.getItem('user_id');

    // If token exists but Redux state is empty, restore session
    if (token && userId) {
      dispatch(userProfile());
      dispatch(fetchUserSpecificData(userId));
    }
  }, [dispatch]);

  // Show onboarding popover for first-time users
  useEffect(() => {
    if (loggedUId) {
      const hasSeenChatOnboarding = localStorage.getItem('hasSeenChatOnboarding');

      if (!hasSeenChatOnboarding) {
        // Show popover after 2 seconds delay for better UX
        const timer = setTimeout(() => {
          setShowOnboarding(true);
        }, 2000);

        return () => clearTimeout(timer);
      }
    }
  }, [loggedUId]);
  

// --- NEW: Global Socket Listener for Notifications ---
useEffect(() => {
  if (loggedUserInfo?.user_id) {
    // 1. Fetch initial count
    dispatch(fetchUnreadCount(loggedUserInfo.user_id));

    // 2. Connect socket if needed
    const token = getUserToken();
    if (token && !socket.connected) {
      connectWithToken(token);
    }

    // 3. Listen for incoming messages
    const handleReceiveGlobal = (msg) => {
      // If chat is CLOSED, increment unread count
      // (If chat is OPEN, ChatPopup handles logic)
      if (!chatOpen) {
        dispatch(incrementUnreadCount());
      }
    };

    socket.on("receiveMessage", handleReceiveGlobal);

    return () => {
      socket.off("receiveMessage", handleReceiveGlobal);
    };
  }
}, [loggedUserInfo, chatOpen, dispatch]);

const colorMode = useMemo(() => ({
  // toggleColorMode REMOVED
  toggleTheme: () => {
    setThemeName((prev) => {
      const nextTheme = prev === 'modern' ? 'classic' : 'modern';
      localStorage.setItem('themeName', nextTheme);
      return nextTheme;
    });
  },
}), []);

const getDesignTokens = (mode, themeName) => {
  if (themeName === 'modern') {
    return {
      palette: {
        mode: 'light', // Force light
        primary: { main: '#054A49', contrastText: '#ffffff' }, // Deep Emerald
        secondary: { main: '#D4AF37', contrastText: '#121212' }, // Soft Gold
        background: { default: '#FAFAF9' }, // Always light bg
        text: { primary: '#1B263B' },
        custom: {
          navbarBg: '#054A49', // Revert to Deep Emerald
          navbarText: '#FAFAF9',
          logoColor: '#D4AF37',
          sectionBorder: '1px solid rgba(113, 18, 62, 0.1)',
          mainGradient: 'linear-gradient(135deg, #054A49 0%, #0a6967 100%)',
          secondaryGradient: 'linear-gradient(135deg, #f3cf55 0%, #D4AF37 100%)',
          heroOverlay: 'linear-gradient(to top, rgba(5, 74, 73, 0.8), rgba(5, 74, 73, 0.6))',
        }
      },
      typography: {
        fontFamily: "'Lato', 'Inter', 'Noto Sans Sinhala', sans-serif",
        h1: { fontFamily: "'Playfair Display', 'Abhaya Libre', serif", fontWeight: 700 },
        h2: { fontFamily: "'Playfair Display', 'Abhaya Libre', serif", fontWeight: 700 },
        h3: { fontFamily: "'Playfair Display', 'Abhaya Libre', serif", fontWeight: 600 },
        h4: { fontFamily: "'Playfair Display', 'Abhaya Libre', serif", fontWeight: 600 },
        button: { textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em' },
      },
      shape: { borderRadius: 4 },
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: 4,
              boxShadow: 'none',
              padding: '12px 28px',
              '&:hover': {
                boxShadow: 'none',
                backgroundColor: '#043d3c',
              },
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: 'none',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
              border: '1px solid rgba(0,0,0,0.05)',
            },
          },
        },
        MuiAppBar: {
          styleOverrides: {
            root: {
              boxShadow: 'none',
              borderBottom: '1px solid rgba(0,0,0,0.08)',
            }
          }
        }
      },
    };
  } else {
    // CLASSIC THEME
    return {
      palette: {
        mode: 'light', // Force light
        primary: { main: '#71123e', contrastText: '#ffffff' }, // Merlot
        secondary: { main: '#C3937C', contrastText: '#000000' }, // Rose Gold
        background: { default: '#fff' },
        text: { primary: '#000' },
        custom: {
          navbarBg: '#71123e', // Merlot (User requested #71123e)
          navbarText: '#ffffff', // White font for legible contrast
          logoColor: '#D4AF37', // Gold logo to match fonts
          sectionBorder: '1px solid rgba(113, 18, 62, 0.1)',
          mainGradient: 'linear-gradient(135deg, #71123e 0%, #8b1a4f 100%)',
          secondaryGradient: 'linear-gradient(135deg, #EAD9C9 0%, #C3937C 100%)',
          heroOverlay: 'linear-gradient(to top, rgba(113, 18, 62, 0.6), rgba(113, 18, 62, 0.3))',
        }
      },
      typography: {
        fontFamily: "'Roboto', 'Noto Sans Sinhala', sans-serif",
        h1: { fontFamily: "'Roboto', 'Abhaya Libre', serif", fontWeight: 700 },
        h2: { fontFamily: "'Roboto', 'Abhaya Libre', serif", fontWeight: 700 },
        h3: { fontFamily: "'Roboto', 'Abhaya Libre', serif", fontWeight: 600 },
        h4: { fontFamily: "'Roboto', 'Abhaya Libre', serif", fontWeight: 600 },
        button: { textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em' },
      },
      shape: { borderRadius: 4 },
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: 4,
              boxShadow: 'none',
              padding: '12px 28px',
              '&:hover': {
                boxShadow: 'none',
                backgroundColor: '#5e0e32',
              },
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: 'none',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
              border: '1px solid rgba(0,0,0,0.05)',
            },
          },
        },
        MuiAppBar: {
          styleOverrides: {
            root: {
              boxShadow: 'none',
              borderBottom: '1px solid rgba(0,0,0,0.08)',
            }
          }
        }
      },
    };
  }
};

const appTheme = useMemo(() => createTheme(getDesignTokens(mode, themeName)), [mode, themeName]);

// persist language changes
const handleSetLanguage = (val) => {
  localStorage.setItem('language', val);
  setLanguage(val);
  window.dispatchEvent(new StorageEvent('storage', { key: 'language', newValue: val }));
};

// ensure body has current mode class on initial render
if (typeof document !== 'undefined') {
  document.body.dataset.mode = 'light'; // Force light
}

return (
  <ThemeProvider theme={appTheme}>
    <CssBaseline />
    <ScrollToTop />
    <GlobalStyles styles={{
      ':root': {
        '--brand-primary': appTheme.palette.primary.main,
        '--brand-secondary': appTheme.palette.secondary.main,
        '--bg-default': appTheme.palette.background.default,
        '--text-primary': appTheme.palette.text.primary,
        '--navbar-bg': appTheme.palette.custom.navbarBg,
        '--navbar-text': appTheme.palette.custom.navbarText,
        '--logo-color': appTheme.palette.custom.logoColor,
        '--section-border': appTheme.palette.custom.sectionBorder,
        '--main-gradient': appTheme.palette.custom.mainGradient,
        '--secondary-gradient': appTheme.palette.custom.secondaryGradient,
        '--hero-overlay': appTheme.palette.custom.heroOverlay,
      },
      'html, body': {
        overflowX: 'hidden',
        maxWidth: '100%',
        width: '100%',
      },
      'body': {
        fontFamily: appTheme.typography.fontFamily,
        color: 'var(--text-primary)',
        backgroundColor: 'var(--bg-default)',
        fontWeight: 400,
        letterSpacing: '0.01em',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
      },
      '*': {
        boxSizing: 'border-box',
      },
      'h1, h2, h3, h4, h5, h6': {
        fontFamily: "'Playfair Display', serif",
        fontWeight: 700,
        letterSpacing: '0em',
        lineHeight: 1.25,
      },
      'p, span, div': {
        letterSpacing: '0.01em',
        lineHeight: 1.6,
      },
      // Removed dark mode styles
    }} />
    {/* <Router> */}
    <Navbar
      mode={mode}
      toggleTheme={colorMode.toggleTheme}
      currentTheme={themeName}
      language={language}
      setLanguage={handleSetLanguage}
    />
    <FavoriteListView />
    <ActionAlerts />
    <Routes>
      <Route path='/' element={<Home />} />
      <Route path='/service-bride' element={<ServiceBride language={language} />} />
      <Route path='/service-groom' element={<ServiceGroom language={language} />} />
      <Route path='/signUp' element={<SignUp language={language} />} />
      <Route path='/login' element={<Login language={language} />} />
      <Route path='/verify-email' element={<EmailVerification />} />
      <Route path='/dashboard' element={<Dashboard />} />
      <Route path='/pricing-page' element={<PricingPage />} />
      <Route path='/about' element={<About language={language} />} />
      <Route path='/login-phone' element={<PhAuth />} />
      <Route path='/Infor-page' element={<InforPage language={language} />} />
      <Route path='/payment-dayfinder' element={<AdminLogin />} />
      <Route path='/day-finder' element={<DayFinder />} />
      <Route path='/payment-dayfinder' element={<PaymentDay />} />
      <Route path='/login-admin' element={<AdminLogin />} />
      <Route path='/dashboard-admin' element={<AdminDashboard />} />
      <Route path='/terms-and-conditions' element={<TermsAndConditions language={language} />} />
      <Route element={<ProtectedRoute />}>
        <Route path='/moreDetailsPage/:loggedUId/:detailId' element={<MoreDetailsPage />} />
        <Route path='/payment/:loggedUId' element={<PaymentPage />} />
        <Route path='/products' element={<Products />} />
        <Route path='/service/:loggedUId' element={<UserDetails />} />
        <Route path='/profile' element={<UserProfile />} />
        <Route path='/change-password' element={<ChangePassword />} />
      </Route>
    </Routes>
    <Footer language={language} />


    {/* Global Scroll Buttons */}
    <Box sx={{ position: 'fixed', bottom: 100, right: 24, display: 'flex', flexDirection: 'column', gap: 2, zIndex: 1000 }}>
      <Fab
        size="small"
        color="primary"
        aria-label="scroll-up"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        sx={{ opacity: 0.8, '&:hover': { opacity: 1 } }}
      >
        <ArrowUpwardIcon />
      </Fab>
      <Fab
        size="small"
        color="secondary"
        aria-label="scroll-down"
        onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}
        sx={{ opacity: 0.8, '&:hover': { opacity: 1 } }}
      >
        <ArrowDownwardIcon />
      </Fab>
    </Box>

    {/* Enhanced Global Chat Button - Only show if user is logged in */}
    {loggedUId && (
      <Tooltip
        title={unreadCount > 0 ? `${unreadCount} new message${unreadCount > 1 ? 's' : ''}!` : "Chat with your matches"}
        placement="left"
        arrow
        sx={{
          '& .MuiTooltip-tooltip': {
            fontSize: '0.875rem',
            backgroundColor: appTheme.palette.primary.main,
            padding: '8px 16px',
          },
          '& .MuiTooltip-arrow': {
            color: appTheme.palette.primary.main,
          }
        }}
      >
        <Fab
          color="primary"
          aria-label="chat"
          onClick={() => setChatOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1000,
            background: appTheme.palette.custom.mainGradient,
            width: 64,
            height: 64,
            boxShadow: '0 8px 24px rgba(5, 74, 73, 0.4)',
            animation: unreadCount > 0
              ? 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
              : 'gentlePulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite',
            '@keyframes pulse': {
              '0%, 100%': {
                transform: 'scale(1)',
                boxShadow: '0 8px 24px rgba(5, 74, 73, 0.4)',
              },
              '50%': {
                transform: 'scale(1.08)',
                boxShadow: '0 12px 32px rgba(212, 175, 55, 0.6)',
              },
            },
            '@keyframes gentlePulse': {
              '0%, 100%': {
                transform: 'scale(1)',
              },
              '50%': {
                transform: 'scale(1.03)',
              },
            },
            '&:hover': {
              background: 'linear-gradient(135deg, #043d3c 0%, #054A49 100%)',
              transform: 'scale(1.1)',
              boxShadow: '0 12px 32px rgba(212, 175, 55, 0.5)',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -2,
              left: -2,
              right: -2,
              bottom: -2,
              background: appTheme.palette.custom.secondaryGradient,
              borderRadius: '50%',
              zIndex: -1,
              opacity: unreadCount > 0 ? 0.6 : 0,
              animation: unreadCount > 0 ? 'glowPulse 2s ease-in-out infinite' : 'none',
            },
            '@keyframes glowPulse': {
              '0%, 100%': {
                opacity: 0.3,
                transform: 'scale(1)',
              },
              '50%': {
                opacity: 0.7,
                transform: 'scale(1.15)',
              },
            },
          }}
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            max={99}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.8rem',
                fontWeight: 700,
                minWidth: 22,
                height: 22,
                padding: '0 6px',
                backgroundColor: '#ff3d47',
                color: '#fff',
                border: '2px solid #fff',
                boxShadow: '0 2px 8px rgba(255, 61, 71, 0.5)',
              }
            }}
          >
            <ChatIcon sx={{ fontSize: 32, color: '#fff' }} />
          </Badge>
        </Fab>
      </Tooltip>
    )}

    {/* First-Time User Onboarding Popover */}
    <Popover
      open={showOnboarding && !!loggedUId}
      anchorReference="anchorPosition"
      anchorPosition={{ top: window.innerHeight - 100, left: window.innerWidth - 450 }}
      onClose={() => {
        setShowOnboarding(false);
        localStorage.setItem('hasSeenChatOnboarding', 'true');
      }}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      PaperProps={{
        sx: {
          maxWidth: 360,
          background: 'linear-gradient(135deg, #054A49 0%, #0a6967 100%)',
          color: '#fff',
          borderRadius: 3,
          boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
          border: '2px solid #D4AF37',
          animation: 'slideInRight 0.5s ease-out',
          '@keyframes slideInRight': {
            '0%': {
              transform: 'translateX(100px)',
              opacity: 0,
            },
            '100%': {
              transform: 'translateX(0)',
              opacity: 1,
            },
          },
        }
      }}
    >
      <Box sx={{ p: 3, position: 'relative' }}>
        <IconButton
          onClick={() => {
            setShowOnboarding(false);
            localStorage.setItem('hasSeenChatOnboarding', 'true');
          }}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: '#fff',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.1)',
            }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, pr: 3 }}>
          {language === 'si' ? '💬 නව විශේෂාංගය!' : '💬 New Feature!'}
        </Typography>

        <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
          {language === 'si'
            ? 'ඔබේ ප්‍රියතම ප්‍රොෆයිල සමඟ සජීවීව කතාබස් කරන්න! පහළ දකුණු කෙළවරේ ඇති කතාබහ බොත්තම ක්ලික් කරන්න.'
            : 'Chat live with your favorite profiles! Click the chat button at the bottom right to start a conversation.'
          }
        </Typography>

        <Button
          fullWidth
          variant="contained"
          onClick={() => {
            setShowOnboarding(false);
            localStorage.setItem('hasSeenChatOnboarding', 'true');
            setChatOpen(true);
          }}
          sx={{
            background: appTheme.palette.custom.secondaryGradient,
            color: '#000',
            fontWeight: 700,
            py: 1.5,
            '&:hover': {
              background: 'linear-gradient(135deg, #D4AF37 0%, #f3cf55 100%)',
              transform: 'scale(1.02)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          {language === 'si' ? 'කතාබස් අරඹන්න' : 'Start Chatting'}
        </Button>

        <Typography
          variant="caption"
          sx={{
            display: 'block',
            textAlign: 'center',
            mt: 1.5,
            opacity: 0.8,
            fontSize: '0.7rem'
          }}
        >
          {language === 'si' ? 'මෙය එක් වරක් පමණක් පෙන්වයි' : 'This will only show once'}
        </Typography>
      </Box>
    </Popover>

    {/* Global Chat Popup */}
    {loggedUId && (
      <ChatPopup
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        currentUserId={loggedUId}
        targetUser={{ id: detailId, name: userName }}
      />
    )}
    {/* </Router> */}
  </ThemeProvider>
);
}
export default App;
